<template>
  <div class="fill-height d-flex flex-column align-center justify-center bg-white">
    <div class="d-flex flex-column align-center row-gap-6">
      <img
        alt="Closd Logo"
        class="closd-logo"
        :class="{ 'pinned ma-6': !isPending }"
        src="@/assets/img/closd_logo.svg"
      />

      <template v-if="isPending">
        <div class="d-flex flex-column row-gap-4">
          <h3 v-if="displayedPendingTitle">{{ displayedPendingTitle }}</h3>
          <v-progress-linear
            indeterminate
          />
        </div>
      </template>
      <template v-else-if="errorToDisplay">
        <img src="@/assets/img/auth_error.svg" alt="" class="auth-error-image" />
        <div class="d-flex flex-column align-center row-gap-4">
          <h1>{{ $t(`${TRANSLATION_KEY_PREFIX}.errors.${errorToDisplay}.title`) }}</h1>
          <p class="text-center" v-html="$t(`${TRANSLATION_KEY_PREFIX}.errors.${errorToDisplay}.text`)"></p>
        </div>
        <app-button
          color="primary"
          @click="forceSignInView"
        >
          {{ $t(`${TRANSLATION_KEY_PREFIX}.errors.${errorToDisplay}.linkLabel`) }}
        </app-button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'

import { getUserId, setUserId } from '@/common/utils/localUserId'
import {
  ROOM_ROUTE_TYPES,
  SIMPLE_SIGNATURE_ROUTE_NAME,
  SIGNING_VIEW_ROUTE_NAME,
  NOTIFICATIONS_PREFERENCES_ROUTE_NAME,
  PROFILE_SETTINGS_ROUTE_NAME,
  LOGOUT_ROUTE_NAME,
  SIGN_IN_ROUTE_NAME,
  TOKEN_SCOPES,
} from '@/router/routerData'
import { GET_AUTH_FROM_TOKEN } from '@/store/modules/login/action_types'
import { SET_USER_TOKEN_SCOPE } from '@/store/modules/login/mutation_types'
import { SET_USER_PROFILE } from '@/store/modules/user/mutation_types'

const TRANSLATION_KEY_PREFIX = 'login.views.AuthByToken'

export default {
  name: 'AuthByToken',
  props: {
    envelopeId: String,
    mnemo: String,
    token: {
      type: String,
      required: true,
    },
    withoutApiToken: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      authenticationInProgress: true,
      redirectInProgress: false,
      errorToDisplay: null,
    }
  },
  computed: {
    displayedPendingTitle() {
      if(this.authenticationInProgress)
      return this.$t('login.views.AuthByToken.authInProgress')

      if(this.redirectInProgress)
      return this.$t('login.views.AuthByToken.redirectInProgress')

      return null
    },
    isPending () {
      return this.authenticationInProgress || this.redirectInProgress
    },
    purpose () {
      for (const value of Object.values(TOKEN_SCOPES)) {
        if (value === this.$route.name) {
          return value
        }
      }
      return null
    },
    routeName () {
      return this.$route.name
    },
  },
  mounted () {
    const lang = window.navigator.language
    if (lang.includes('-')) {
      this.$i18n.locale = lang.split('-')[0]
    } else {
      this.$i18n.locale = lang
    }

    // For notifications preferences link in emails, we don't login user with a token anymore, we keep this redirection here
    // for emails that have already been sent to users, so the link in them still works.
    if (this.routeName === NOTIFICATIONS_PREFERENCES_ROUTE_NAME) {
      this.redirectToProfileSettings()
      return
    }

    if (this.purpose && this.token) {
      this.prepareGetAuthFromToken()
    }
  },
  methods: {
    ...mapActions('login', [GET_AUTH_FROM_TOKEN]),
    ...mapMutations('login', [SET_USER_TOKEN_SCOPE]),
    async prepareGetAuthFromToken () {
      try {
        const response = await this.GET_AUTH_FROM_TOKEN({
          params: {
            purpose: this.purpose,
            token: this.token,
          },
          withoutApiToken: this.$route.query.withoutApiToken?? this.withoutApiToken,
        })
        setUserId(response.data.user.id)
        this.SET_USER_TOKEN_SCOPE(response.data.scopes)
        this.authenticationInProgress = false
        this.redirectInProgress = true
        this.$store.commit(`user/${SET_USER_PROFILE}`, response.data.user)
        this.redirectToRightView()
      } catch (error) {
        if (['ERR_INCORRECT_DATA', 'ERR_TOKEN_EXPIRED'].includes(error.response?.data?.errorCode)) {
          this.errorToDisplay = error.response.data.errorCode
        } else {
          this.errorToDisplay = 'ERR_DEFAULT'
        }
      } finally {
        this.authenticationInProgress = false
      }
    },
    redirectToRightView () {
      if (this.routeName === SIMPLE_SIGNATURE_ROUTE_NAME) {
        this.$router.replace({
          name: SIGNING_VIEW_ROUTE_NAME,
          params: {
            envelopeId: this.envelopeId,
            mnemo: this.mnemo,
            roomType: ROOM_ROUTE_TYPES.PROJECT,
            token: this.token,
          },
        })
      }
    },
    redirectToProfileSettings () {
      const profileSettingsPath = this.$router.resolve({ name: PROFILE_SETTINGS_ROUTE_NAME }).fullPath
      // /profile/settings requires auth but the app has no navigation guard enforcing it.
      // Without local auth state the user would land on the connected layout and get stuck on a permanent loader,
      // so we route unauthenticated users through sign-in and bring them back after login.
      if (!getUserId()) {
        this.$router.replace({
          name: SIGN_IN_ROUTE_NAME,
          params: {
            locale: this.$i18n.locale,
          },
          query: {
            returnRoute: profileSettingsPath,
          },
        })

        return
      }

      this.$router.replace({
        name: PROFILE_SETTINGS_ROUTE_NAME,
      })
    },
    forceSignInView () {
      this.$router.replace({ name: LOGOUT_ROUTE_NAME })
    },
  },
}
</script>

<style lang="scss" scoped>
.closd-logo {
  width: 150px;

  &.pinned {
    left: 0;
    position: absolute;
    top: 0;
    width: 120px;
  }
}
.auth-error-image {
  max-width: 112px;
  height: auto;
}
</style>
